import { isPinned } from './runtimeLimitsForm.js';
import { formatShortDate } from './runtimeLimitsUsage.js';

const MICRO_PER_DOLLAR = 1_000_000;
const MICRO_PER_CENT = 10_000;

/** Caps the page edits, in display order. The runtime-limits list supplies their values and bounds. */
export const CAP_KEYS = [
  'flockbot_global_hard_monthly_micro_usd',
  'flockbot_global_soft_monthly_micro_usd',
  'flockbot_org_daily_micro_usd',
  'flockbot_user_daily_micro_usd',
];

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

export function microToDollars(micro) {
  return Number(micro) / MICRO_PER_DOLLAR;
}

/** Dollars to the cent; any spend under half a cent reads "<$0.01" instead of a misleading "$0.00". */
export function formatMicroDollars(micro) {
  const value = Number(micro) || 0;
  if (value > 0 && value < MICRO_PER_CENT / 2) return '<$0.01';
  return microToDollars(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Percent of `capMicro` that `micro` is; null when the cap is 0 (paused). Can pass 100. */
export function percentOfCap(micro, capMicro) {
  if (!capMicro) return null;
  return (Number(micro) / capMicro) * 100;
}

export function formatPercent(percent) {
  if (percent === null || percent === undefined) return '—';
  if (percent > 0 && percent < 1) return '<1%';
  return `${Math.round(percent)}%`;
}

export function sharePercent(part, whole) {
  return whole > 0 ? (part / whole) * 100 : 0;
}

export function formatMonth(month) {
  const match = MONTH_PATTERN.exec(month ?? '');
  if (!match) return month || '—';
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}

/** The current month (from `today`, YYYY-MM-DD) and the `count - 1` months before it, newest first. */
export function monthOptions(today, count = 12) {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(today ?? '');
  if (!match) return [];
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  return Array.from({ length: count }, (_, back) => {
    const date = new Date(Date.UTC(year, monthIndex - back, 1));
    const value = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    return { value, label: formatMonth(value) };
  });
}

/**
 * What the tiles show. Spend counts in-flight reservations, the same total the breakers compare
 * against. `level` is 'hard' at or past the hard cap, 'soft' past the soft cap (or a soft cap of 0), else 'ok'.
 */
export function summarize(report) {
  const { totals, caps, days } = report;
  const isCurrentMonth = report.today?.slice(0, 7) === report.month;
  const spentMicroUsd = totals.spentMicroUsd + totals.reservedMicroUsd;
  let level = 'ok';
  if (spentMicroUsd >= caps.hardMonthlyMicroUsd) level = 'hard';
  else if (caps.softMonthlyMicroUsd === 0 || spentMicroUsd > caps.softMonthlyMicroUsd) level = 'soft';

  return {
    isCurrentMonth,
    spentMicroUsd,
    reservedMicroUsd: totals.reservedMicroUsd,
    percentOfHard: percentOfCap(spentMicroUsd, caps.hardMonthlyMicroUsd),
    level,
    projectedMicroUsd: isCurrentMonth ? totals.projectedMonthEndMicroUsd : totals.spentMicroUsd,
    todayMicroUsd: totals.todaySpentMicroUsd,
    dailyAverageMicroUsd: days.length ? Math.round(totals.spentMicroUsd / days.length) : 0,
    calls: totals.calls,
    isEmpty: totals.calls === 0 && spentMicroUsd === 0,
  };
}

/** Chart rows in dollars: each day's spend (bars) and the month's running total (line). */
export function chartRows(days) {
  return days.map((day) => ({
    date: day.date,
    label: formatShortDate(day.date),
    dailyDollars: microToDollars(day.spentMicroUsd + day.reservedMicroUsd),
    cumulativeDollars: microToDollars(day.cumulativeMicroUsd),
    calls: day.calls,
  }));
}

/** The editable caps from a runtime-limits list response, in CAP_KEYS order. Keys the server lacks are skipped. */
export function capLimits(limits) {
  return CAP_KEYS.map((key) => limits.find((limit) => limit.key === key)).filter(Boolean);
}

/** A µUSD value as editor text, e.g. 25000000 -> "25.00". */
export function dollarsInputText(micro) {
  return microToDollars(micro).toFixed(2);
}

export function capRangeMessage(limit) {
  return `Enter a dollar amount from ${formatMicroDollars(limit.foldMin)} through ${formatMicroDollars(
    limit.foldMax,
  )}, with at most 2 decimal places.`;
}

/** Editor text in dollars to whole µUSD, or null when it is not a non-negative amount with at most 2 decimals inside the key's Fold bounds. */
export function parseDollarsToMicro(text, limit) {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(text ?? '').trim());
  if (!match) return null;
  const micro = Number(match[1]) * MICRO_PER_DOLLAR + Number((match[2] ?? '').padEnd(2, '0')) * MICRO_PER_CENT;
  if (!Number.isSafeInteger(micro) || micro < limit.foldMin || micro > limit.foldMax) return null;
  return micro;
}

function capWarning(limit, next, monthSpentMicroUsd) {
  if (next === 0) return '0 turns this off everywhere now, until you raise it.';
  if (limit.kind === 'monthly_spend' && Number.isFinite(monthSpentMicroUsd) && next <= monthSpentMicroUsd) {
    return `This month's spend (${formatMicroDollars(monthSpentMicroUsd)}) is already at or above this cap, so it applies on the next question.`;
  }
  return null;
}

/**
 * What saving a dollars `draft` against the `limit` snapshot would do, in the shape
 * runtimeLimitsForm's buildUpdateBody and outcomeHolds take. `value` is the wire µUSD
 * (null = revert). Acknowledgement mirrors the server: a raise, a first pin, or a revert.
 * `monthSpentMicroUsd` is this month's spend, for the already-past warning on monthly caps.
 */
export function planCapChange(limit, draft, monthSpentMicroUsd) {
  if (draft.target === 'revert') {
    return {
      ok: true,
      value: null,
      next: limit.fallbackValue,
      change: 'revert',
      needsAcknowledgement: true,
      warning: capWarning(limit, limit.fallbackValue, monthSpentMicroUsd),
    };
  }

  const value = parseDollarsToMicro(draft.text, limit);
  if (value === null) return { ok: false, message: capRangeMessage(limit) };

  let change;
  if (!isPinned(limit)) change = 'pin';
  else if (value === limit.value) change = 'unchanged';
  else change = value > limit.value ? 'raise' : 'lower';

  return {
    ok: true,
    value,
    next: value,
    change,
    needsAcknowledgement: change === 'pin' || change === 'raise',
    warning: change === 'unchanged' ? null : capWarning(limit, value, monthSpentMicroUsd),
  };
}
